import { Button, Color, Flex, Spacing, Text } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { useConfirmFecMappings } from '../../hooks/useConfirmFecMappings'
import { useFetchFecAccountMappings } from '../../hooks/useFetchFecAccountMappings'
import { useFetchFecJournalMappings } from '../../hooks/useFetchFecJournalMappings'
import { useFetchFecThirdPartyMappings } from '../../hooks/useFetchFecThirdPartyMappings'
import { useTrackFecImport } from '../../hooks/useTrackFecImport'
import { formatFecImportPeriod } from '../../utils/formatFecImportPeriod'
import { getFecThirdPartyStatusCounts } from '../../utils/getFecThirdPartyStatusCounts'
import { hasFecMappingsToConfirm } from '../../utils/hasFecMappingsToConfirm'
import { isFecConfirmationIncomplete } from '../../utils/isFecConfirmationIncomplete'
import * as Styled from './styles'

interface FecImportFinaliseStepProps {
  onConfirmSuccess: () => void
  onReviewMappings: () => void
}

export const FecImportFinaliseStep = ({
  onConfirmSuccess,
  onReviewMappings,
}: FecImportFinaliseStepProps): ReactElement => {
  const { i18n, t } = useTranslation()
  const { sessionFecImports: fecImports } = useFecImportSessionContext()
  const { confirm, isError, isProcessing } = useConfirmFecMappings(fecImports)
  const { trackThirdPartiesConfirmed } = useTrackFecImport()

  const fecImportIds = useMemo(() => fecImports.map((fecImport) => fecImport.id), [fecImports])
  const {
    accountMappings,
    isError: isAccountMappingsError,
    isLoading: isAccountMappingsLoading,
  } = useFetchFecAccountMappings(fecImportIds)
  const {
    isError: isJournalMappingsError,
    isLoading: isJournalMappingsLoading,
    journalMappings,
  } = useFetchFecJournalMappings(fecImportIds)
  const {
    isError: isThirdPartyMappingsError,
    isLoading: isThirdPartyMappingsLoading,
    thirdPartyMappings,
  } = useFetchFecThirdPartyMappings(fecImportIds)

  // A count is only real once its list is loaded; while loading or after a failed fetch the list is empty, not zero.
  const getCount = (count: number, isLoading: boolean, isError: boolean): number | string =>
    isLoading || isError ? '–' : count

  const period = useMemo(() => {
    const periodStarts = fecImports
      .flatMap(({ periodStart }) => (periodStart ? [periodStart] : []))
      .sort((periodStart, otherPeriodStart) => periodStart.localeCompare(otherPeriodStart))
    const periodEnds = fecImports
      .flatMap(({ periodEnd }) => (periodEnd ? [periodEnd] : []))
      .sort((periodEnd, otherPeriodEnd) => periodEnd.localeCompare(otherPeriodEnd))

    return formatFecImportPeriod(periodStarts[0], periodEnds.at(-1), i18n.language)
  }, [fecImports, i18n.language])

  const handleReviewMappingsClick = useCallback(() => {
    onReviewMappings()
  }, [onReviewMappings])

  const handleConfirmClick = useCallback(async () => {
    // Everything is confirmed already, e.g. a request that failed on the client after the backend applied it.
    if (!hasFecMappingsToConfirm(fecImports)) {
      onConfirmSuccess()

      return
    }

    try {
      // Tracked only when the third parties themselves were still open, not on a retry of the entry push alone.
      const hasThirdPartiesToConfirm = fecImports.some(({ thirdPartyMappingStatus, thirdPartyPushStatus }) =>
        isFecConfirmationIncomplete(thirdPartyMappingStatus, thirdPartyPushStatus),
      )

      await confirm()

      if (hasThirdPartiesToConfirm && !isThirdPartyMappingsLoading && !isThirdPartyMappingsError) {
        trackThirdPartiesConfirmed({ fecImportIds, ...getFecThirdPartyStatusCounts(thirdPartyMappings) })
      }

      onConfirmSuccess()
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)

      trackError(new Error(`FEC mappings confirmation failed: ${message}`))
    }
  }, [
    confirm,
    fecImportIds,
    fecImports,
    isThirdPartyMappingsError,
    isThirdPartyMappingsLoading,
    onConfirmSuccess,
    thirdPartyMappings,
    trackThirdPartiesConfirmed,
  ])

  return (
    <Styled.FinaliseStepWrapper>
      <Styled.SummaryContent>
        <Styled.Summary>
          <Flex flexDirection="column" gap={Spacing.S8}>
            <Text colorVariant="primary" variant="h2">
              {t('settings.organization.accounting.fec_file_import.modal.finalise_step.title')}
            </Text>
            {period && (
              <Text colorVariant="secondary" variant="h3">
                {period}
              </Text>
            )}
          </Flex>

          <Styled.NumberCards>
            <Styled.NumberCard>
              <Text colorVariant="primary" variant="h3">
                {getCount(journalMappings.length, isJournalMappingsLoading, isJournalMappingsError)}
              </Text>
              <Text colorVariant="secondary" variant="bodyMD">
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.journals')}
              </Text>
            </Styled.NumberCard>
            <Styled.NumberCard>
              <Text colorVariant="primary" variant="h3">
                {getCount(accountMappings.length, isAccountMappingsLoading, isAccountMappingsError)}
              </Text>
              <Text colorVariant="secondary" variant="bodyMD">
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.chart_of_accounts')}
              </Text>
            </Styled.NumberCard>
            <Styled.NumberCard>
              <Text colorVariant="primary" variant="h3">
                {getCount(thirdPartyMappings.length, isThirdPartyMappingsLoading, isThirdPartyMappingsError)}
              </Text>
              <Text colorVariant="secondary" variant="bodyMD">
                {t('settings.organization.accounting.fec_file_import.modal.mapping_step.tabs.third_party_accounts')}
              </Text>
            </Styled.NumberCard>
          </Styled.NumberCards>

          {isError && (
            <Text color={Color.Red700} variant="bodyMD">
              {t('settings.organization.accounting.fec_file_import.modal.finalise_step.confirm_error')}
            </Text>
          )}
        </Styled.Summary>
      </Styled.SummaryContent>

      <Flex justifyContent="space-between" width="100%">
        <Button disabled={isProcessing} icon="arrowLeft" onClick={handleReviewMappingsClick} variant="outlined">
          {t('settings.organization.accounting.fec_file_import.modal.finalise_step.review_mappings_button')}
        </Button>
        <Button
          disabled={isProcessing}
          iconRight="arrowRight"
          loading={isProcessing}
          onClick={handleConfirmClick}
          variant="filled"
        >
          {t('settings.organization.accounting.fec_file_import.modal.finalise_step.confirm_button')}
        </Button>
      </Flex>
    </Styled.FinaliseStepWrapper>
  )
}
